import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hammer, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(
    typeof window !== "undefined" && localStorage.getItem("cd_remember") === "1",
  );
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    localStorage.setItem("cd_remember", rememberMe ? "1" : "0");
    const res = await login(email, password, rememberMe);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav("/", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F4F3EF]">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7167061/pexels-photo-7167061.jpeg"
          alt="Modern kitchen"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white/15 backdrop-blur grid place-items-center">
              <Hammer className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading text-lg font-bold tracking-tight">Cabinet Depot</div>
              <div className="text-[11px] tracking-[0.2em] uppercase opacity-80">Ops Console</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-[1.05]">
              Every order. Every dollar. Every cabinet.
            </h1>
            <p className="mt-4 text-white/85 leading-relaxed">
              From paint shop to delivery van — track orders, costs, and profit in one calm console.
            </p>
          </div>
          <div className="text-[11px] tracking-[0.18em] uppercase opacity-75">
            Cabinets · Kitchens · Appliances
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <Hammer className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-bold">Cabinet Depot</div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground">Ops Console</div>
            </div>
          </div>

          <div className="label-eyebrow mb-3">Sign in</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Welcome back.</h2>
          <p className="text-muted-foreground mt-2 mb-8">
            Use your team credentials to open the console.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
              />
            </div>
            <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
                data-testid="login-remember-me"
              />
              Keep me signed in for 30 days
            </label>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-primary hover:bg-[#203A2C] text-primary-foreground h-11"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
